import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Qualité",
  "lead": "L’éducation de bonne qualité ne peut être assimilée ni réduite aux résultats d’apprentissage. Trop nombreux sont les enfants privés d’enseignants suffisamment formés, d’infrastructures scolaires satisfaisantes ou d’un environnement d’apprentissage sûr et non violent."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, HighlightByIndex, IndicatorReference, QualityFramework, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky, Tooltip} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!QualityFramework) _missingMdxReference("QualityFramework", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  if (!Tooltip) _missingMdxReference("Tooltip", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_QUALITY"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "L’éducation de bonne qualité ne peut être assimilée ni réduite aux résultats d’apprentissage. Trop nombreux sont les enfants privés d’enseignants suffisamment formés, d’infrastructures scolaires satisfaisantes ou d’un environnement d’apprentissage sûr et non violent."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Il est difficile d’évaluer l’éducation de bonne qualité car chaque pays en a sa propre interprétation et sa propre définition. Hormis les résultats d’apprentissage, il n’existe pas beaucoup d’indicateurs mesurant la qualité au regard de l’objectif mondial en matière d’éducation, l’ODD 4."
    }), "\n", _jsx(_components.h2, {
      id: "enseignants",
      children: "Enseignants"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.c.2",
      children: [_jsx(_components.p, {
        children: "Les enseignants sont la pierre angulaire de la qualité de l’éducation. Cependant, après 2000, l’élan impulsé en faveur de l’éducation primaire universelle a conduit un grand nombre d’élèves supplémentaires sur les bancs de l’école sans que le recrutement des enseignants ne progresse au même rythme. Cela s’est traduit par des classes surchargées et le recul de la qualité de l’éducation."
      }), _jsx(QualityFramework, {
        highlighted: "teachers",
        description: "En savoir plus sur le cadre relatif à la qualité de l’éducation"
      }), _jsx(_components.p, {
        children: "Le graphique ci-dessous présente le nombre d’élèves par enseignant à l’école. Dans certains pays d’Afrique subsaharienne, ce rapport s’améliore depuis 2000 mais reste plus défavorable que dans les années 1980."
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "PTR"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["En ", _jsx(HighlightByIndex, {
              index: "0",
              children: "Afrique subsaharienne"
            }), ", après tous les efforts menés entre 1990 et 2000 en vue de rendre l’école primaire gratuite, les enfants ont intégré l’école plus rapidement que les pays n’ont été capables de recruter de nouveaux enseignants. Le nombre d’élèves par enseignant s’est donc souvent envolé."]
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["En ", _jsx(HighlightByIndex, {
              index: "6",
              children: "Amérique du Nord et en Europe"
            }), ", en revanche, le rapport est établi à environ 14 élèves par enseignant depuis 2005."]
          })
        })]
      })]
    }), "\n", _jsx(_components.p, {
      children: "L’enseignement est une mission exigeante et les enseignants qui n’y sont pas bien préparés arrivent en classe avec un sérieux handicap. Il existe pour les pays deux moyens essentiels d’évaluer leur état de préparation. Il s’agit d’examiner :"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Premièrement, s’ils sont ", _jsx(_components.strong, {
          children: "qualifiés"
        }), ", c’est à-dire s’ils détiennent ou non les diplômes universitaires requis selon les normes nationales."]
      }), "\n", _jsxs(_components.li, {
        children: ["Deuxièmement, s’ils sont ", _jsx(_components.strong, {
          children: "formés"
        }), ", ce qui, indépendamment de leurs qualifications, permet d’évaluer s’ils ont suivi ou non une formation des enseignants appropriée."]
      }), "\n"]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.c.1",
      children: [_jsxs(_components.p, {
        children: ["Pour un enseignant, être formé signifie avoir reçu une formation permettant d’acquérir des ", _jsx(Tooltip, {
          title: "Pedagogical knowledge",
          content: "Knowledge on approaches, methods and techniques of teaching.",
          children: "connaissances pédagogiques"
        }), " et des ", _jsx(Tooltip, {
          title: "Professional knowledge",
          content: "Knowledge on, for example, the legal frameworks governing the teaching profession.",
          children: "connaissances professionnelles"
        }), ". Certains programmes portent également sur la ", _jsx(Tooltip, {
          title: "Content knowledge",
          content: "Knowledge of the curriculum, subject matter and use of relevant materials.",
          children: "connaissance de contenu"
        }), ". Mais le type de formation requise diffère selon les pays, et les informations en la matière sont rares. En raison de ces différences, l’indicateur relatif aux enseignants formés [4.c.1] est l’un des moins comparables du cadre de suivi de l’ODD 4, alors qu’il s’agit d’une dimension essentielle de l’éducation de qualité.\nÀ l’échelle mondiale, la majorité des enseignants sont qualifiés, formés, voire les deux. En Amérique latine et dans les Caraïbes, par exemple, 80 % des enseignants sont formés."]
      }), _jsxs(_components.p, {
        children: ["En ", _jsx(HighlightByIndex, {
          index: "0",
          children: "Afrique subsaharienne"
        }), ", en revanche, le pourcentage d’enseignants formés est passé de 85 % en 2000 à 69 % en 2022."]
      }), _jsx(Figure, {
        id: "TRAINED"
      }), _jsx(_components.p, {
        children: "Il n’existe pas de solution miracle pour les pays en pénurie d’enseignants qualifiés et formés. Beaucoup manquent d’enseignants qualifiés pour répondre à la demande en raison du faible nombre de diplômés du tertiaire en général. Cela montre à quel point les problèmes peuvent se répercuter les uns sur les autres au sein d’un système éducatif : des problèmes à un certain niveau, comme un faible taux d’achèvement des études secondaires, peuvent facilement entraîner d’autres problèmes ailleurs, et se traduire par exemple par un nombre restreint d’enseignants formés pour les prochaines générations d’élèves."
      })]
    }), "\n", _jsx(_components.h2, {
      id: "infrastructure",
      children: "Infrastructure"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1e",
      children: [_jsxs(_components.p, {
        children: ["Un environnement propice à l’apprentissage doit être équipé d’installations d’approvisionnement en ", _jsx(_components.strong, {
          children: "eau"
        }), " et d’assainissement adéquates, en particulier pour les filles. Cependant, un accès élémentaire à l’eau n’était assuré que dans 55 % des écoles primaires en Afrique subsaharienne, 88 % en Asie de l’Est et du Sud-Est et 85 % en Asie centrale et du Sud."]
      }), _jsx(QualityFramework, {
        highlighted: "material",
        description: "En savoir plus sur le cadre relatif à la qualité de l’éducation"
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1f",
      children: [_jsxs(_components.p, {
        children: ["Plus particulièrement, les installations ", _jsx(_components.strong, {
          children: "sanitaires"
        }), " qui manquent d’intimité peuvent sembler peu sûres, tandis que celles qui ne permettent pas d’observer une bonne hygiène menstruelle peuvent empêcher les filles d’aller à l’école. L’accès à des toilettes séparées est donc une mesure politique essentielle pour éviter que la scolarisation tardive ne se généralise à l’école primaire et que les filles abandonnent l’école au fil du temps."]
      }), _jsx(_components.p, {
        children: "Le graphique ci-dessous montre que moins de la moitié des écoles primaires sont pourvues d’installations sanitaires non mixtes, non seulement en Afrique subsaharienne mais aussi dans des pays asiatiques comme l'Afghanistan, la RDP lao et le Népal. En Indonésie, seuls 64 % des écoles primaires, 76 % des établissements du premier cycle du secondaire et 78 % de ceux du deuxième cycle du secondaire offrent un accès à des sanitaires séparés."
      }), _jsx(Figure, {
        id: "SANITATION"
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1a",
      children: [_jsxs(_components.p, {
        children: ["L’", _jsx(_components.strong, {
          children: "électricité"
        }), " est un besoin essentiel pour disposer d’un environnement d’apprentissage de qualité, pourtant de nombreux pays n’y ont pas accès. Dans les pays à faible revenu, 72 % des écoles primaires et 57 % des établissements du deuxième cycle du secondaire ne disposent pas de l’électricité."]
      }), _jsx(_components.p, {
        children: "Comme l’illustre le graphique ci-dessous, moins de la moitié des écoles primaires ont accès à l’électricité au Nicaragua et au Pakistan. Dans certains pays d’Afrique subsaharienne, l’accès à l’électricité est très rare dans les écoles : 12 % des écoles en disposent à Madagascar, et seulement 1 % au Tchad. Sans électricité, l’éclairage peut être insuffisant et les enseignants ne peuvent pas utiliser d’ordinateurs pour les tâches administratives ou la formation. L’absence d’électricité signifie qu’il n’y aura pas de ventilation, de climatisation ou de chauffage, ce qui peut rendre les salles de classe impropres à l’enseignement et à l’apprentissage."
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1b",
      children: [_jsxs(_components.p, {
        children: ["L’absence d’électricité signifie également qu’il est impossible de se servir des technologies et d’", _jsx(_components.strong, {
          children: "Internet"
        }), ". Cependant, l’électricité n’est pas le seul facteur à ralentir la diffusion d’Internet. Dans les pays à revenu intermédiaire, 36 % des écoles primaires et 20 % des établissements de deuxième cycle du secondaire disposent de l’électricité mais pas d’Internet. Même si les écoles sont raccordées à une source d’électricité, les surtensions et baisses de tension sont fréquentes. La connectivité à Internet dépend des infrastructures de télécommunications nationales, de la situation géographique et des moyens financiers de l’école pour payer ce service."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "ELECTRIC"
          })
        }), _jsx(ScrollySection, {
          id: "LKA",
          children: _jsxs(_components.p, {
            children: ["Au ", _jsx(_components.strong, {
              children: "Sri Lanka"
            }), ", si la totalité des écoles primaires sont raccordées à l’électricité, seules 41 % ont accès à Internet. Il en va de même pour 42 % des écoles au Turkménistan."]
          })
        }), _jsx(ScrollySection, {
          id: "ECU,URY",
          children: _jsxs(_components.p, {
            children: ["Dans de nombreux pays d’Amérique latine, des écoles sont privées d’électricité et d’Internet. Seules environ 60 % des écoles en ", _jsx(_components.strong, {
              children: "Équateur"
            }), " et 65 % en Argentine peuvent affirmer avoir accès aux deux. La seule exception est l’", _jsx(_components.strong, {
              children: "Uruguay"
            }), ", où 100 % des écoles primaires sont équipées des deux."]
          })
        })]
      })]
    }), "\n", _jsx(_components.h2, {
      id: "violence",
      children: "Violence"
    }), "\n", _jsx(_components.p, {
      children: "Les actes de violence ou les menaces en milieu scolaire peuvent être de nature psychologique, physique ou sexuelle. Ils peuvent se produire dans l’enceinte des établissements scolaires mais aussi sur le trajet de l’école, à la maison ou dans le cyberespace. Ces violences naissent souvent d’un rapport de force inégal et découlent fréquemment de normes et de stéréotypes négatifs en matière de genre."
    }), "\n", _jsx(_components.p, {
      children: "Si l’attention est généralement retenue par les événements extrêmes, comme les fusillades, ce sont les formes de violence plus ordinaires et qui passent souvent inaperçues, telles que le harcèlement, qui ont les effets les plus néfastes sur l’éducation des enfants et des adolescents. Les données sur ces sujets sont encore insuffisantes car les questions posées pour recueillir les données proposent des définitions diverses du comportement violent, portent sur des périodes différentes, ou s’appuient sur des choix de réponse, des dispositions en matière de confidentialité ou des protocoles de déontologie impossibles à comparer. À ceci s’ajoute le fait que certaines formes de violence, comme le harcèlement homophobe, font moins souvent l’objet de signalement en raison de leur caractère tabou."
    }), "\n", _jsx(QualityFramework, {
      highlighted: "school",
      description: "En savoir plus sur le cadre relatif à la qualité de l’éducation"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.2",
      children: [_jsxs(_components.p, {
        children: ["Le ", _jsx(_components.strong, {
          children: "harcèlement"
        }), " est la forme de violence en milieu scolaire la plus étudiée. Il peut s’agir de violences physiques, de violences verbales ou de l’intention de causer des dommages psychologiques par l’humiliation et l’exclusion. La figure ci-après montre qu’au moins un adolescent sur trois, davantage parmi les garçons que chez les filles, est victime de harcèlement dans la plupart des pays."]
      }), _jsx(Figure, {
        id: "BULLY_FIGHT"
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["La ", _jsx(_components.strong, {
        children: "violence physique"
      }), " englobe le fait d’être mêlé à des bagarres à l’école, de porter une arme (arme à feu, couteau ou batte) ou d’en être menacé dans l’enceinte de l’école, de faire partie d’une bande ou d’en être complice ou bien de subir des violences physiques de la part d’un membre du personnel scolaire."]
    }), "\n", _jsx(_components.p, {
      children: "Dans 24 pays, au moins 10 % des garçons de 15 ans ont déclaré avoir été impliqués dans des bagarres physiques trois fois ou plus en 2022, et la prévalence a atteint plus de 30 % en Arménie."
    }), "\n", _jsx(_components.h2, {
      id: "conclusion",
      children: "Conclusion"
    }), "\n", _jsx(_components.p, {
      children: "Dans le même temps, les données comparables sur le plan international en ce qui concerne les indicateurs relatifs aux enseignants sont étonnamment rares. Relativement peu de pays effectuent le dénombrement, même élémentaire, des enseignants, et ils ne prennent pas en compte les heures d’enseignement, les enseignants occupant des postes administratifs et d’autres réalités complexes. Il est nécessaire d’établir une typologie des normes concernant les enseignants formés afin de pouvoir correctement repérer les lacunes et porter cette question à un niveau mondial."
    }), "\n", _jsx(_components.p, {
      children: "La mise en place d'établissements scolaires « adaptés aux enfants, aux personnes handicapées et aux deux sexes » et d'un « cadre d'apprentissage sûr, non violent, inclusif et efficace pour tous » est un défi dans de nombreux pays, comme l’illustre cet article. Dans le même temps, il est difficile de mesurer les progrès en la matière car l’indicateur mondial correspondant n’est pas une mesure simple, mais regroupe plusieurs dimensions."
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.oecd.org/en/publications/education-at-a-glance-2025_1c0d9c79-en.html",
            children: "Education at a glance – OECD"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://theirworld.org/news/safe-schools-report-reveals-crises-challenges-action-needed-to-educate-children",
            children: "Safe Schools Report – Their World"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://unesdoc.unesco.org/ark:/48223/pf0000232107",
            children: "School-related gender-based violence is preventing the achievement of quality education for all"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
